/**
 * Hero Slideshow
 * Handles fade transitions, autoplay, progress bar,
 * keyboard navigation, touch/swipe, and pause on hover.
 */

class HeroSlideshow {
  constructor(el) {
    this.el          = el;
    this.slides      = [...el.querySelectorAll('[data-slide-index]')];
    this.dots        = [...el.querySelectorAll('[data-hero-dot]')];
    this.prevBtn     = el.querySelector('[data-hero-prev]');
    this.nextBtn     = el.querySelector('[data-hero-next]');
    this.progress    = el.querySelector('.hero__progress-bar');
    this.counterNum  = el.querySelector('[data-hero-current-num]');

    this.current   = 0;
    this.total     = this.slides.length;
    this.autoplay  = el.dataset.autoplay === 'true';
    this.delay     = parseInt(el.dataset.delay, 10) || 6000;
    this.timer     = null;
    this.paused    = false;

    if (this.total > 1) this._init();
    this._scheduleNudge();
  }

  _init() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    this.el.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.prev(); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.next(); }
    });

    this._addSwipe();

    this.el.addEventListener('mouseenter', () => { this.paused = true;  this._pause(); });
    this.el.addEventListener('mouseleave', () => { this.paused = false; this._play();  });

    this.el.addEventListener('focusin',  () => { this.paused = true;  this._pause(); });
    this.el.addEventListener('focusout', () => { this.paused = false; this._play();  });

    if (this.autoplay) this._play();
  }

  goTo(index) {
    if (index === this.current) return;

    const prev = this.current;
    this.current = ((index % this.total) + this.total) % this.total;

    this.slides[prev].classList.remove('hero__slide--active');
    this.dots[prev]?.classList.remove('hero__dot--active');
    this.dots[prev]?.setAttribute('aria-selected', 'false');

    this.slides[this.current].classList.add('hero__slide--active');
    this.dots[this.current]?.classList.add('hero__dot--active');
    this.dots[this.current]?.setAttribute('aria-selected', 'true');

    if (this.counterNum) {
      this.counterNum.textContent = String(this.current + 1).padStart(2, '0');
    }

    this._resetProgress();

    this._handleVideoPlayback(prev, this.current);
  }

  next() { this.goTo(this.current + 1); }
  prev() { this.goTo(this.current - 1); }

  _play() {
    if (!this.autoplay || this.paused || this.total <= 1) return;
    this._pause();
    this.timer = setInterval(() => this.next(), this.delay);
  }

  _pause() {
    clearInterval(this.timer);
    this.timer = null;
  }

  _resetProgress() {
    if (!this.progress) return;
    this.progress.style.animation = 'none';
    this.progress.offsetHeight; /* reflow to restart animation */
    this.progress.style.animationDuration = `${this.delay / 1000}s`;
    this.progress.style.animation = '';
  }

  _handleVideoPlayback(prevIndex, nextIndex) {
    const prevSlide = this.slides[prevIndex];
    const nextSlide = this.slides[nextIndex];

    prevSlide.querySelectorAll('video').forEach(v => { v.pause(); });
    nextSlide.querySelectorAll('video').forEach(v => {
      v.currentTime = 0;
      v.play().catch(() => {});
    });
  }

  _addSwipe() {
    let startX = 0;
    let startY = 0;
    let touchingMosaic = false;

    this.el.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      // If the touch starts inside a mosaic, let the mosaic's native
      // scroll handle it — don't intercept for slide navigation.
      touchingMosaic = !!e.target.closest('[data-mosaic]');
    }, { passive: true });

    this.el.addEventListener('touchend', (e) => {
      if (touchingMosaic) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
        dx < 0 ? this.next() : this.prev();
      }
    }, { passive: true });
  }

  /* ── Mobile "peek" nudge — montre qu'on peut swiper ── */
  _scheduleNudge() {
    if (window.matchMedia('(min-width: 768px)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // const STORAGE_KEY = 'hero_nudged';
    // if (!window.Shopify?.designMode && sessionStorage.getItem(STORAGE_KEY)) return;
    // sessionStorage.setItem(STORAGE_KEY, '1');

    const INIT_DELAY = 900;

    /* Cas 1 : slide active = mosaic → nudge du scroll horizontal */
    const mosaic = this.el.querySelector('.hero__slide--active .hero__mosaic');
    if (mosaic) {
      this._nudgeMosaic(mosaic, INIT_DELAY);
      return;
    }

    /* Cas 2 : plusieurs slides classiques → nudge de transition */
    if (this.total >= 2) {
      this._nudgeSlides(INIT_DELAY);
    }
  }

  /* Nudge : scroll horizontal de la mosaic (tac tac) */
  _nudgeMosaic(mosaic, delay) {
    const PEEK1 = 55;   // px 1er nudge
    const PEEK2 = 32;   // px 2e nudge (plus petit = effet amorti)
    const DUR   = 350;  // ms par mouvement (plus doux)
    const PAUSE = 120;  // pause entre les 2 nudges

    const animate = (from, to, duration, easing, cb) => {
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min((now - t0) / duration, 1);
        let e;
        if (easing === 'out') {
          e = 1 - Math.pow(1 - p, 3); // ease-out cubic
        } else {
          e = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2; // ease-in-out cubic
        }
        mosaic.scrollLeft = from + (to - from) * e;
        if (p < 1) requestAnimationFrame(tick);
        else if (cb) cb();
      };
      requestAnimationFrame(tick);
    };

    setTimeout(() => {
      if (mosaic.scrollLeft > 5) return;

      mosaic.style.scrollSnapType = 'none';

      // Nudge 1 : aller (ease-out, départ rapide) puis retour (ease-in-out, doux)
      animate(0, PEEK1, DUR, 'out', () => {
        setTimeout(() => {
          animate(PEEK1, 0, DUR, 'inout', () => {
            setTimeout(() => {
              // Nudge 2 : plus petit, même logique
              animate(0, PEEK2, DUR, 'out', () => {
                setTimeout(() => {
                  animate(PEEK2, 0, DUR, 'inout', () => {
                    mosaic.style.scrollSnapType = '';
                  });
                }, Math.round(DUR * 0.5));
              });
            }, PAUSE);
          });
        }, Math.round(DUR * 0.5));
      });
    }, delay);
  }

  /* Nudge : slides classiques — peek visuel de la slide 2 */
  _nudgeSlides(delay) {
    const DUR   = 260;
    const PAUSE = 160;
    const PEEK_X = '18%';

    setTimeout(() => {
      const s0 = this.slides[0];
      const s1 = this.slides[1];

      this._pause();

      s1.style.cssText = 'transition:none;opacity:1;transform:translateX(100%);z-index:2;';
      s0.style.cssText = 'transition:none;transform:translateX(0);';
      void s0.offsetHeight;

      const ease = `transform ${DUR}ms cubic-bezier(0.4,0,0.6,1)`;

      s0.style.transition = ease;
      s1.style.transition = ease;
      s0.style.transform = `translateX(-${PEEK_X})`;
      s1.style.transform = `translateX(calc(100% - ${PEEK_X}))`;

      setTimeout(() => {
        s0.style.transform = 'translateX(0)';
        s1.style.transform = 'translateX(100%)';

        setTimeout(() => {
          s0.style.transform = `translateX(-${PEEK_X})`;
          s1.style.transform = `translateX(calc(100% - ${PEEK_X}))`;

          setTimeout(() => {
            s0.style.transform = 'translateX(0)';
            s1.style.transform = 'translateX(100%)';

            setTimeout(() => {
              s1.style.transition = 'none';
              s1.style.opacity = '0';
              void s1.offsetHeight;
              s1.style.cssText = '';
              s0.style.cssText = '';
              if (this.autoplay) this._play();
            }, DUR);
          }, DUR);
        }, PAUSE);
      }, DUR);
    }, delay);
  }
}

/* ── Init all hero sections on the page ── */
function initHeroes() {
  document.querySelectorAll('[data-hero]').forEach(el => new HeroSlideshow(el));
}

/*
 * Activation des vidéos mosaic par IntersectionObserver.
 * Les <source data-src="..."> sont vides tant que la cellule n'est pas visible.
 * Sur mobile (mosaic horizontal) : seule la 1ère cellule est visible au chargement →
 * 1 vidéo chargée au lieu de 6. Les autres se lancent au scroll.
 */
function _startMosaicVideo(video) {
  video.querySelectorAll('source[data-src]').forEach(function(s) {
    s.setAttribute('src', s.dataset.src);
    delete s.dataset.src;
  });
  video.load();

  /* Masquer le poster img quand la vidéo est prête à jouer */
  video.addEventListener('canplay', function() {
    var poster = video.nextElementSibling;
    if (poster && poster.classList.contains('hero__mosaic-img--video-poster')) {
      poster.style.opacity = '0';
      setTimeout(function() { poster.style.display = 'none'; }, 320);
    }
  }, { once: true });
}

function activateMosaicVideos() {
  var cells = document.querySelectorAll('.hero__mosaic-cell');
  if (!cells.length) return;

  var staggerDelay = 0;

  cells.forEach(function(cell) {
    var video = cell.querySelector('.hero__mosaic-video');
    if (!video || !video.querySelector('source[data-src]')) return;

    var rect = cell.getBoundingClientRect();
    var inView = rect.left < window.innerWidth &&
                 rect.right > 0 &&
                 rect.top < window.innerHeight &&
                 rect.bottom > 0;

    if (inView) {
      /* Cellules visibles : chargement échelonné (800ms entre chaque) pour
         ne pas saturer la bande passante avec 6 vidéos HD simultanées */
      setTimeout(function() { _startMosaicVideo(video); }, staggerDelay);
      staggerDelay += 800;
    } else {
      /* Cellules hors viewport : IntersectionObserver strict (0px margin) */
      var obs = new IntersectionObserver(function(entries) {
        if (entries[0].isIntersecting) {
          _startMosaicVideo(video);
          obs.disconnect();
        }
      }, { rootMargin: '0px' });
      obs.observe(cell);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroes);
} else {
  initHeroes();
}

/*
 * Activation vidéo différée : on attend le load event (ressources critiques
 * téléchargées, LCP mesuré) avant de déclencher le download des vidéos.
 * Évite la compétition bande passante entre la vidéo HD et le poster LCP.
 */
if (document.readyState === 'complete') {
  setTimeout(activateMosaicVideos, 300);
} else {
  window.addEventListener('load', function() {
    setTimeout(activateMosaicVideos, 300);
  }, { once: true });
}

/* ── Re-init after Shopify theme editor section updates ── */
document.addEventListener('shopify:section:load', (e) => {
  const hero = e.target.querySelector('[data-hero]');
  if (hero) new HeroSlideshow(hero);
  activateMosaicVideos();
});
